import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthGuard } from '@/hooks/useAuthGuard'

export type ProTier = 'free' | 'annual' | 'lifetime' | 'god' | 'earned'

interface ProTierInfo {
  /** Kullanıcının mevcut tier'ı */
  tier: ProTier
  /** Pro mu? (tier !== 'free') — backward compat */
  isPro: boolean
  /** God mode mu? */
  isGod: boolean
  /** Maksimum aktif grup sayısı */
  maxGroups: number
  /** Pro süre bitiş tarihi (annual için) */
  expiresAt: string | null
  /** Yükleniyor mu? */
  loading: boolean
}

// Tier → max grup limiti mapping
const TIER_LIMITS: Record<ProTier, number> = {
  free: 1,
  annual: 11,
  earned: 11,
  lifetime: Infinity,
  god: Infinity,
}

/**
 * 💎 useProTier — Tier-aware Pro/Free kullanıcı kontrol hook'u
 * 
 * users.pro_tier üzerinden çalışır.
 * Free=1 grup, Annual=11 grup, Lifetime/God=Sınırsız
 * 
 * Backward compat: isPro = tier !== 'free'
 */
export function useProTier(): ProTierInfo {
  const { user: authUser } = useAuthGuard()
  const [tier, setTier] = useState<ProTier>('free')
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!authUser?.id) {
      setLoading(false)
      return
    }

    // Session cache ile hızlı yükleme
    const cached = sessionStorage.getItem(`proTier_${authUser.id}`)
    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        setTier(parsed.tier)
        setExpiresAt(parsed.expiresAt)
        setLoading(false)
        return
      } catch {
        // ignore
      }
    }

    const fetchTier = async () => {
      try {
        const { data } = await supabase
          .from('users')
          .select('pro_tier, pro_expires_at, is_pro, referral_code, total_successful_referrals, has_unseen_pro_reward')
          .eq('id', authUser.id)
          .maybeSingle()

        // Fallback: is_pro=true ama pro_tier='free' ise → lifetime olarak kabul et
        let userTier = (data?.pro_tier as ProTier) || 'free'
        if (userTier === 'free' && data?.is_pro === true) {
          userTier = 'lifetime'
        }
        const expires = data?.pro_expires_at || null

        setTier(userTier)
        setExpiresAt(expires)

        sessionStorage.setItem(`proTier_${authUser.id}`, JSON.stringify({
          tier: userTier,
          expiresAt: expires,
        }))
      } catch {
        setTier('free')
      } finally {
        setLoading(false)
      }
    }

    fetchTier()
  }, [authUser?.id])

  const isPro = tier !== 'free'
  const isGod = tier === 'god'
  const maxGroups = TIER_LIMITS[tier]

  return { tier, isPro, isGod, maxGroups, expiresAt, loading }
}

/**
 * 💎 useIsPro — Backward compatible shim
 * Mevcut useIsPro referanslarının kırılmaması için korunuyor.
 */
export function useIsPro(): { isPro: boolean; loading: boolean } {
  const { isPro, loading } = useProTier()
  return { isPro, loading }
}
